import React, { useEffect, useState } from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicator from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import { Body } from '~/components/Experiences/Home/Content/styles';
import { Field, FlexWrapper, Text } from '~/components/Experiences/components';
import { useUpdateExperienceValues } from '~/components/Experiences/hooks';

import { ORIGINAL_EXPERIENCE_CONTENT_TAB, themes } from '~/consts/experiences';

import { sortBy } from '~/utils/arrayUtils';

const getHumanizedType = (type: Experiences.BookingField['type']) => {
  switch (type) {
    case 'BIG-TEXT':
      return 'Big text';
    case 'TEXT':
      return 'Short text';
    case 'INTEGER':
      return 'Integer number';
    case 'FLOAT':
      return 'Decimal number';
    case 'BOOLEAN':
      return 'Boolean';
    case 'DATE':
      return 'Date';
    case 'PHONE':
      return 'Phone';
    case 'SIMPLE-SELECT':
      return 'Single option';
    case 'MULTI-SELECT':
      return 'Multi options';
    case 'EMAIL':
      return 'Email';
    default:
      return type;
  }
};

function SortableList({ items, areOriginalValues, onValueChange, onOptionChange, onDragEnd }) {
  return (
    <DndContext onDragEnd={onDragEnd}>
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <Body height="500px">
          {items.map((bookingField) => (
            <SortableItem
              key={bookingField.id}
              bookingField={bookingField}
              onValueChange={onValueChange}
              onOptionChange={onOptionChange}
              areOriginalValues={areOriginalValues}
            />
          ))}
        </Body>
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ bookingField, onOptionChange, onValueChange, areOriginalValues }) {
  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id: bookingField.id,
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  return (
    <Box
      border={`1px solid ${themes?.primaryLight}`}
      p="5px"
      mb="10px"
      display="flex"
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <FlexWrapper mb="10px" style={{ flexWrap: 'wrap' }}>
        <FlexWrapper style={{ flexDirection: 'row', width: '100%', marginBottom: '20px' }}>
          <FlexWrapper style={{ flexDirection: 'column', width: '20%' }}>
            <Text weight={400} title={bookingField.id} mb="10px">
              ID
            </Text>
            <Text weight={600} title={bookingField.id}>
              {bookingField.id}
            </Text>
          </FlexWrapper>
          <FlexWrapper style={{ flexDirection: 'column', width: '20%' }}>
            <Text weight={400} title={bookingField.id} mb="10px">
              Type
            </Text>
            <Text weight={600} title={bookingField.id}>
              {getHumanizedType(bookingField.type)}
            </Text>
          </FlexWrapper>
          <FlexWrapper style={{ flexDirection: 'column', width: '20%' }}>
            <Text weight={400} title={bookingField.id} mb="10px">
              Required on provider
            </Text>
            <Text weight={600} title={bookingField.id}>
              {bookingField.required ? 'Yes' : 'No'}
            </Text>
          </FlexWrapper>
          <FlexWrapper style={{ flexDirection: 'column', width: '20%' }}>
            <Text weight={400} title={bookingField.id} mb="10px">
              Sort
            </Text>
            <Text weight={600} title={bookingField.id}>
              #{bookingField.sort}
            </Text>
          </FlexWrapper>
        </FlexWrapper>

        <FlexWrapper style={{ marginBottom: '20px' }}>
          <FlexWrapper style={{ width: '30%' }} mr="10px">
            <Field
              style={{ width: '100%' }}
              name="label"
              onChange={({ value }) => onValueChange(bookingField.id, { label: value })}
              labelName="Label"
              value={bookingField.label}
              disabled={areOriginalValues}
            />
          </FlexWrapper>
          <FlexWrapper style={{ width: '30%' }} mr="10px">
            <Field
              style={{ width: '100%' }}
              name="placeholder"
              onChange={({ value }) => onValueChange(bookingField.id, { placeholder: value })}
              labelName="Placeholder"
              value={bookingField.placeholder}
              disabled={areOriginalValues}
            />
          </FlexWrapper>
          <FlexWrapper style={{ width: '30%', flexDirection: 'row' }} mr="10px">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bookingField.showOnCheckout}
                    disabled={areOriginalValues}
                    onChange={(event) => onValueChange(bookingField.id, { showOnCheckout: event.target.checked })}
                  />
                }
                label="Show on checkout"
              />
            </FormGroup>
          </FlexWrapper>
        </FlexWrapper>

        {!!bookingField.options && (
          <FlexWrapper style={{ flexDirection: 'column', alignItems: 'start' }}>
            <FlexWrapper style={{ width: '100%' }}>
              <Text weight={400} title={bookingField.id} mb="10px">
                Options
              </Text>
            </FlexWrapper>
            {bookingField.options.map((option, optionIndex) => (
              <FlexWrapper style={{ width: '20%' }} key={optionIndex}>
                <Field
                  style={{ width: '100%' }}
                  name="placeholder"
                  onChange={({ value }) => onOptionChange(bookingField.id, optionIndex, { label: value })}
                  labelName={option.value}
                  value={option.label}
                  disabled={areOriginalValues}
                />
              </FlexWrapper>
            ))}
          </FlexWrapper>
        )}
      </FlexWrapper>

      <Box {...listeners}>
        <DragIndicator color="primary" fontSize="medium" sx={{ cursor: 'grab' }} />
      </Box>
    </Box>
  );
}

function ExperienceCurationBookingFields() {
  const { contentTab, updateValues, values } = useUpdateExperienceValues();
  const [sortedItems, setSortedItems] = useState<Array<Experiences.BookingField>>([]);

  const areOriginalValues = contentTab === ORIGINAL_EXPERIENCE_CONTENT_TAB;

  const handleValue = (id: string, data: Partial<Experiences.BookingField>) => {
    setSortedItems((sortedItems) => {
      const index = sortedItems.findIndex((item) => item.id === id);

      sortedItems[index] = {
        ...sortedItems[index],
        ...data,
      };
      updateValues({
        bookingFields: sortedItems,
      });

      return sortedItems;
    });
  };

  const handleOptions = (id: string, optionIndex, data: Partial<Experiences.BookingField['options'][number]>) => {
    setSortedItems((sortedItems) => {
      const index = sortedItems.findIndex((item) => item.id === id);

      sortedItems[index].options[optionIndex] = {
        ...sortedItems[index].options[optionIndex],
        ...data,
      };

      updateValues({
        bookingFields: sortedItems,
      });

      return sortedItems;
    });
  };

  const handleSort = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active?.id || !over?.id) {
      return;
    }

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
    const newIndex = sortedItems.findIndex((item) => item.id === over.id);

    const newBookingsFields = arrayMove(sortedItems, oldIndex, newIndex).map((item, index) => ({
      ...item,
      sort: index + 1,
    }));

    setSortedItems(newBookingsFields);
    updateValues({ bookingFields: newBookingsFields });
  };

  useEffect(() => {
    if (values && !sortedItems.length) {
      const sorted = sortBy(values.bookingFields, (item) => item.sort, 'asc');
      setSortedItems(sorted);
    }
  }, [sortedItems.length, values]);

  return (
    <Accordion variant="outlined" disabled={!sortedItems.length}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>Booking field</AccordionSummary>
      <AccordionDetails>
        <SortableList
          items={sortedItems}
          areOriginalValues={areOriginalValues}
          onOptionChange={handleOptions}
          onValueChange={handleValue}
          onDragEnd={handleSort}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default ExperienceCurationBookingFields;
